import type { Option } from "@tento-lona/sheets";
import type { NaiveDate } from "@tento-chrono";
import { Rational } from "@tento-core/rational";
import { Tracing } from "@tento-core/tracing";
import {
  RowTypeCatalog,
  TimedGridConfig,
  type CellEntry,
  type CellValue,
  type ChildSpec,
  type HeaderSecondRowKind,
  Json,
  type JsonObject,
  type Role,
} from "@tento-lona/sheets";
import {
  type CellCardinality,
  DATA_GRAPH_ROW_HEIGHTS,
  type DataPresetSpec,
  type RowLayoutHeights,
} from "@tento-lona/sheets/platform";
import { RowTypeSpecOps } from "@tento-lona/sheets/platform";
import { SheetRegistries } from "@tento-lona/sheets";
import { resolveLayoutShape } from "@tento-lona/sheets/platform";
import type { CellLayoutOptions, RendererId } from "@tento-lona/sheets";

import { Network } from "../network.ts";
import { CALENDAR_SYSTEM_PROVIDER } from "../secondary-calendars/provider.ts";
import type { LayoutSpec, Timescale } from "../view-model/view-model.ts";
import type { SheetRow } from "./sheet-row.ts";

/**
 * Compose a `LayoutSpec` for a row by combining caller-supplied per-type
 * heights with the rows-side `layoutShape` lookup.
 *
 * Heights are owned by the descriptor (DOM-side) — callers pass the heights
 * they want, with any per-row override (`heightPx`) already merged in.
 * Anything missing falls through to `DEFAULT_LAYOUT`.
 *
 * Shape (`stackable`, `spannable`, `minWidthTimescale`) is owned by the
 * rows-side `RowTypeSpec.layoutShape` registry because rows-side tree
 * flattening / CLI consumers need it without dragging pixel heights along.
 */
function composeLayoutSpec(
  rowType: string,
  sheetTimescale: Timescale,
  attributes: Option<JsonObject>,
  heights: Partial<RowLayoutHeights>,
): LayoutSpec {
  const shape = resolveLayoutShape(rowType, attributes, sheetTimescale);
  return {
    cellHeight: heights.cellHeight ?? DEFAULT_LAYOUT.cellHeight,
    maxStackHeight: heights.maxStackHeight ?? DEFAULT_LAYOUT.maxStackHeight,
    maxStackedCells: heights.maxStackedCells ?? DEFAULT_LAYOUT.maxStackedCells,
    seeMoreHeight: heights.seeMoreHeight ?? DEFAULT_LAYOUT.seeMoreHeight,
    ...shape,
  };
}

// ── DataPresetSpec → ChildSpec conversion ───────────────────────

function dataPresetToChildSpecs(
  specs: readonly DataPresetSpec[],
  parentPreset: Option<string>,
): ChildSpec[] {
  return RowTypeSpecOps.dataPresetToChildSpecs(specs, parentPreset);
}

/**
 * The calendar's child seeds: the timescale-polymorphic `calendar-body`
 * layout hosting the `calendar-list` alias (see `RowType.Calendar`).
 */
const CALENDAR_CHILD_SPECS: readonly ChildSpec[] = [
  {
    preset: "calendar-body",
    type: "calendar-body",
    position: Rational.nth(0),
    storage: "own",
    placement: "child",
    children: [
      {
        preset: "calendar-list",
        type: "alias.calendar-list",
        position: Rational.nth(0),
        storage: "own",
        placement: "child",
      },
    ],
  },
];

// ── RowTypeDescriptor ────────────────────────────────────────────

/**
 * What a "create new row" picker shows for a row type: its id, label,
 * one-line description and preview image. Every `RowTypeDescriptor` carries
 * it; `@tento/lona/sheets-ui`'s `RenderPlugin.Preview` adds the plugin icon.
 */
export interface RowTypePreview {
  readonly id: string;
  readonly label: string;
  readonly description: string;
  readonly previewImageUrl: string;
}

/** Types that go with `RowTypeDescriptor`: `Override`. */
export namespace RowTypeDescriptor {
  /**
   * A render layer's override of a row type's metadata (layout spec, cell
   * layout options, label, scroll offset, aria label), keyed by wire row type
   * and installed through `platformOf(client).registerDescriptorOverrides`.
   * A `RenderPlugin` declares one as its `descriptorOverride`.
   */
  export interface Override {
    layoutSpec?(row: SheetRow, sheetTimescale: Timescale): LayoutSpec;
    cellLayoutOptions?(row: SheetRow): Option<CellLayoutOptions>;
    buildLabel?(row: SheetRow): Option<string>;
    initialScrollTop?(row: SheetRow, sheetTimescale: Timescale): number;
    buildAriaLabel?(
      label: Option<string>,
      column: NaiveDate.Partial,
      cellValue: Option<CellValue>,
    ): Option<string>;
    requiresHeaderSecondRow?(
      sheetTimescale: Timescale,
      attributes: Option<JsonObject>,
    ): boolean;
    rendersChildren?(attributes: Option<JsonObject>): boolean;
  }
}

/**
 * Client-side cell computation interface. When a `RowTypeDescriptor`'s `dataOwner`
 * holds a `CellProvider`, `cell-accessor.ts` wires `rh.setComputeRange()`
 * automatically — no server round-trip, no formula engine required.
 */
export interface CellProvider {
  readonly kind: string;
  computeCells(range: NaiveDate.Range, row: SheetRow): CellEntry[];
}

/** Values that go with `CellProvider`: `is`. */
export namespace CellProvider {
  export function is(v: DataOwner): v is CellProvider {
    return (
      typeof v === "object" &&
      v !== null &&
      "kind" in v &&
      (v as CellProvider).kind !== "alias"
    );
  }
}

/**
 * Alias resolver for rows whose cells come from another row's fetch.
 * The batchKey identifies the source row; all alias rows sharing the
 * same batchKey share a single API call.
 */
export interface AliasResolver {
  readonly kind: "alias";
  /** The row key whose cells are fetched (e.g., ":~lona:calendar:primary:events"). */
  batchKey(row: SheetRow): string;
}

/** Values that go with `AliasResolver`: `is`. */
export namespace AliasResolver {
  export function is(v: DataOwner): v is AliasResolver {
    return (
      typeof v === "object" &&
      v !== null &&
      "kind" in v &&
      (v as AliasResolver).kind === "alias"
    );
  }
}

/**
 * Who owns cell data for this row type.
 *
 * - `"user"` — cells are user-written (text, number, checkmark, formula, graph)
 * - `"provider"` — a server-side cell provider generates data (weather, garmin, logs)
 * - `"layout"` — container row with no cell data (calendar, timeline, group)
 * - `CellProvider` — client-side computed cells, no server round-trip
 * - `AliasResolver` — cells come from another row's fetch, batched by key
 */
export type DataOwner =
  | "user"
  | "provider"
  | "layout"
  | CellProvider
  | AliasResolver;

/**
 * A row type's metadata as the SDK resolves it (`SheetRow.descriptor`): its
 * picker preview, structural role, who owns its cells, and the layout,
 * label and scroll callbacks the renderers read. One per wire row type, in
 * the client's row-type registry (`RowType`). Not to be confused with
 * `@tento/lona/sheets`' `Descriptor`, the rows-side role-discriminated
 * union a model row is built from.
 */
export interface RowTypeDescriptor extends RowTypePreview {
  /**
   * Structural role — the sheets-side `Role` (`source`, `layout`,
   * `renderer`, `alias`, `inert`), so consumers iterating
   * `SheetRow.Joined.Handle.descriptor` can branch by role without reading
   * `row.type` strings. A descriptor that declares none is unclassified:
   * role-based checks fall through for it.
   */
  readonly role?: Role;

  /**
   * Sub-discriminator for `role === "inert"` rows. Distinguishes the
   * specific inert kind ("header", "surface-container", "label",
   * etc.). Undefined on non-inert descriptors.
   */
  readonly kind?: string;

  /**
   * Alias template id for `role === "alias"` rows. Matches the
   * `template` field on the sheets-side AliasDescriptor union member
   * (e.g., "calendar", "tasks", "calendar-list"). Undefined on
   * non-alias descriptors.
   */
  readonly template?: string;

  /**
   * Renderer plugin id for `role === "renderer"` rows. Matches the
   * `rendererId` field on the sheets-side RendererDescriptor union
   * member. Undefined on non-renderer descriptors.
   */
  readonly rendererId?: RendererId;

  /**
   * Returns default attributes for this row type. Receives the original row
   * so defaults can be computed dynamically. The result is merged with
   * instance attributes via reconcileAttributes(). JSON only — children
   * visibility defaults live here; the child seeds are `childSpecs`.
   */
  readonly defaultAttributes?: (row: SheetRow.Input) => JsonObject;

  /**
   * The declarative child seeds for a row of this type (e.g. Data's chart
   * preset follows the row's lookupKey). A type that declares this owns its
   * children: an instance's wire `attributes.childSpecs` is ignored. See
   * `SheetRow.childSpecs`.
   */
  readonly childSpecs?: (row: SheetRow.Input) => readonly ChildSpec[];

  /** Height bounds for this row type. Used by Row.update() to clamp heightPx. */
  readonly heightBounds?: { readonly min: number; readonly max: number };

  /** Who owns cell data for this row type. */
  readonly dataOwner: DataOwner;

  /**
   * How many cells can share the same date in this row.
   *
   * - `"single"` (default): at most one cell per date. Cells route to the
   *   standard store keyed by date DSE. Examples: text, data, task, formula.
   * - `"multi"`: many cells can share the same date, disambiguated by `cell.id`.
   *   Cells route to the custom store keyed by `cell.id`. Examples:
   *   calendar-list (events), scheduling-link (bookings), pomodoro (sessions).
   *
   * Independent of date granularity (Range(Ymd) vs Point(datetime)).
   * Datetime cells always go to the custom store regardless of cardinality
   * (legacy behavior — the standard store is date-granular by construction).
   * Required: every descriptor states it (`simple` says `"single"`), so no
   * reader falls back to a default of its own.
   */
  readonly cellCardinality: CellCardinality;

  /**
   * Attribute keys that affect server-generated cell data. When any of
   * these change, cell data is marked stale and refetched after the
   * server PATCH completes. Only meaningful for `dataOwner: "provider"`.
   */
  readonly dataKeys?: readonly string[];

  /** Whether this row type can be nested under a parent row. */
  readonly nestable: boolean;

  readonly rendersChildren: (attributes: Option<JsonObject>) => boolean;

  /** Returns the active layout key for this row type based on the sheet
   *  timescale. Used by tree flattening to determine which children are
   *  subsumed. E.g., Calendar returns `"calendar"` for day/week and
   *  `"calendarMonth"` for month. */
  readonly activeLayout?: (timescale: Timescale) => string;

  readonly layoutSpec: (
    heightPx: Option<number>,
    sheetTimescale: Timescale,
    attributes: Option<JsonObject>,
    wireType: string,
  ) => LayoutSpec;
  readonly requiresHeaderSecondRow: (
    sheetTimescale: Timescale,
    attributes: Option<JsonObject>,
  ) => boolean;
  readonly headerSecondRowKind: (
    sheetTimescale: Timescale,
    attributes: Option<JsonObject>,
  ) => Option<HeaderSecondRowKind>;
  readonly cellLayoutOptions: (
    attributes: Option<JsonObject>,
  ) => Option<CellLayoutOptions>;
  readonly buildLabel: (
    lookupKey: Option<string>,
    attributes: Option<JsonObject>,
  ) => Option<string>;
  readonly initialScrollTop: (
    heightPx: Option<number>,
    sheetTimescale: Timescale,
    attributes: Option<JsonObject>,
  ) => number;
  readonly buildAriaLabel: (
    label: Option<string>,
    column: NaiveDate.Partial,
    cellValue: Option<CellValue>,
  ) => Option<string>;
}

/**
 * `value` and every object it holds, frozen (functions are left as they
 * are): a row-type descriptor is shared by every row of its type in every
 * client of the process, so none of it may be written.
 */
function deepFreeze<T>(value: T): T {
  if (typeof value !== "object" || value === null || Object.isFrozen(value)) return value;
  Object.freeze(value);
  for (const key of Object.getOwnPropertyNames(value)) deepFreeze(Reflect.get(value, key));
  return value;
}

export namespace RowTypeDescriptor {
  /**
   * `descriptor`, deeply frozen: what every descriptor a row carries is. The
   * built-ins (`RowType.*`, `RowType.get`) are frozen once, at load; a
   * descriptor the SDK derives for a row (a client's registries, a render
   * layer's `Override`) is frozen as it is built.
   */
  export function frozen(descriptor: RowTypeDescriptor): RowTypeDescriptor {
    return deepFreeze(descriptor);
  }
}

const DEFAULT_LAYOUT: LayoutSpec = Object.freeze({
  cellHeight: 32,
  maxStackHeight: null,
  maxStackedCells: null,
  seeMoreHeight: 24,
  stackable: false,
  spannable: false,
  minWidthTimescale: null,
});

const IMG = "/@/static/img/row-previews";

/**
 * Callback form for `SimpleOpts.layout.cellHeight`. Used when the
 * height depends on `heightPx` plus runtime context (sheet
 * timescale and/or row attributes) — e.g. `Calendar`'s per-attribute
 * branches. Static heights should pass a `number` instead.
 *
 * The callback is responsible for its own `heightPx ?? fallback`
 * logic. The number form gets `heightPx ?? cellHeight` automatically.
 */
type CellHeightCallback = (ctx: {
  heightPx: Option<number>;
  sheetTimescale: Timescale;
  attributes: Option<JsonObject>;
}) => number;

interface SimpleLayoutOpts
  extends Omit<Partial<LayoutSpec>, "cellHeight"> {
  cellHeight?: number | CellHeightCallback;
}

/**
 * The persisted hide flag for a row's raw JSON cells: a flat
 * `jsonVisible: false` on the row itself. The row IS the data-json carrier,
 * so the flag needs no namespacing (unlike the alias's
 * `children["data-json"].visible`, where the key names a virtual CHILD
 * row). Not `attributes.visible` — that hides the whole row from the body
 * tree, taking the legend (and the way back) with it.
 *
 * Read by the `Data`/`DataJson` descriptors' `cellHeight` callbacks (a
 * hidden row's lane collapses to `HIDDEN_CELL_HEIGHT`) and by the
 * sheets-ui eye toggle + cell binder. Pure attribute helpers — this module
 * sits below `sheet-row`, so no row types here.
 */
export namespace JsonHide {
  export function isHiddenAttrs(
    attributes: Option<JsonObject>,
  ): boolean {
    return attributes?.["jsonVisible"] === false;
  }

  /** The attribute patch that hides (or shows) a row's JSON lane. */
  export function attrsFor(visibility: "hidden" | "visible"): JsonObject {
    return { jsonVisible: visibility === "visible" };
  }

  /** Collapsed lane height — one legend label row. */
  export const HIDDEN_CELL_HEIGHT = 32;
}

interface SimpleOpts {
  label: string;
  description: string;
  previewImageUrl: string;
  dataOwner: DataOwner;
  layout?: SimpleLayoutOpts;
  requiresHeaderSecondRow?: boolean;
  headerSecondRowKind?: HeaderSecondRowKind;
  cellLayoutOptions?: Option<CellLayoutOptions>;
  nestable?: boolean;
}

const DEFAULT_HEIGHT_BOUNDS = Object.freeze({ min: 24, max: 960 });

/**
 * The cell hints most built-in row types start from: a flat cell (no card,
 * no card border), no padding or margins, a 32px label.
 */
const CELL_LAYOUT_DEFAULTS: CellLayoutOptions = Object.freeze({
  card: false,
  canDrawCardBorder: false,
  padding: "0px",
  marginInline: "0px",
  marginBlock: "0px",
  labelHeightPx: 32,
});

/**
 * A descriptor from `opts`, deeply frozen (a built-in's own members are
 * spread over it and the result frozen again at the registry fill; a
 * `fallback` is this alone).
 */
function simple(id: string, opts: SimpleOpts): RowTypeDescriptor {
  return deepFreeze(simpleDescriptor(id, opts));
}

function simpleDescriptor(id: string, opts: SimpleOpts): RowTypeDescriptor {
  // A copy: the options object a caller passed may be a shared constant
  // (`CELL_LAYOUT_DEFAULTS`), which freezing the descriptor must not freeze.
  const cellLayout: Option<CellLayoutOptions> = opts.cellLayoutOptions == null
    ? null
    : { ...opts.cellLayoutOptions };
  // `opts.layout`'s height fields (`cellHeight`, `seeMoreHeight`,
  // `maxStackHeight`, `maxStackedCells`) are the canonical per-type
  // heights — they flow into `composeLayoutSpec` below, with per-row
  // `heightPx` overriding `cellHeight` (number form) or being passed
  // into the callback (function form).
  //
  // Shape fields (`stackable`, `spannable`, `minWidthTimescale`) on
  // `opts.layout` are NOT consumed here — rows-side tree flattening /
  // CLI consumers need them, so they live in the rows-side
  // `RowTypeSpec.layoutShape` registry. Keep them on `opts.layout`
  // callers as documentation; the rows-side catalog is the source of
  // truth (parity test:
  // `tento/tento-lona-js/tests/layout-shape-parity.deno.test.ts`).
  return {
    id,
    label: opts.label,
    description: opts.description,
    previewImageUrl: opts.previewImageUrl,
    dataOwner: opts.dataOwner,
    cellCardinality: "single",
    nestable: opts.nestable ?? false,
    rendersChildren: () => true,
    layoutSpec: (heightPx, sheetTimescale, attributes, wireType) => {
      const ch = opts.layout?.cellHeight;
      const cellHeight =
        typeof ch === "function"
          ? ch({ heightPx, sheetTimescale, attributes: attributes ?? null })
          : (heightPx ?? ch);
      // Shape keys off the row's actual wire type (threaded from
      // `SheetRow.layoutSpec`), NOT the descriptor's coarse `id`. One
      // descriptor backs many wire types, so keying shape off `id`
      // mis-resolved divergent ones (e.g. `alias.calendar-list`, which used
      // to need a manual `layoutSpec` override). `wireType` is required — the
      // descriptor's `id` is no longer a shape key at all.
      const layout = opts.layout;
      return composeLayoutSpec(wireType, sheetTimescale, attributes ?? null, {
        ...(cellHeight !== undefined ? { cellHeight } : {}),
        ...(layout?.maxStackHeight !== undefined
          ? { maxStackHeight: layout.maxStackHeight }
          : {}),
        ...(layout?.maxStackedCells !== undefined
          ? { maxStackedCells: layout.maxStackedCells }
          : {}),
        ...(layout?.seeMoreHeight !== undefined
          ? { seeMoreHeight: layout.seeMoreHeight }
          : {}),
      });
    },
    requiresHeaderSecondRow: () =>
      opts.headerSecondRowKind != null || (opts.requiresHeaderSecondRow ?? false),
    headerSecondRowKind: () =>
      opts.headerSecondRowKind ??
        (opts.requiresHeaderSecondRow ? "calendar-system" : null),
    cellLayoutOptions: () => cellLayout,
    buildLabel: () => null,
    initialScrollTop: () => 0,
    buildAriaLabel: (label, column, _cellValue) =>
      `${label ?? opts.label}, ${column.toString()}`,
  };
}

function calendarHeaderSecondRowKind(
  sheetTimescale: Timescale,
  attributes: Option<JsonObject>,
): Option<HeaderSecondRowKind> {
  const attrs = TimedGridConfig.withChildOverride(
    attributes ?? {},
    TimedGridConfig.BODY_PRESET,
  );
  if (Json.string(attrs, "type") === "list") return null;
  const isMonthly =
    sheetTimescale === "month" &&
    !(Json.boolean(attrs, "disableMonthlyView") ?? false);
  return isMonthly ? "month-weekday" : "calendar-system";
}

// ── RowType namespace ────────────────────────────────────────────

/**
 * The `RowType` namespace: `Header`, `CalendarSystem`, `Text`, `Number`,
 * `Group`, `Formula`, and 40 more.
 */
export namespace RowType {
  export const Header: RowTypeDescriptor = {
    ...simple("header", {
      label: "Header",
      description: "Renders date headers with navigation controls",
      previewImageUrl: `${IMG}/number@2x.png`,
      dataOwner: "layout",
      layout: { cellHeight: 45 },
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    buildAriaLabel: (label, column) =>
      `${label ?? "Header"}, ${column.toString()}`,
  };

  export const CalendarSystem: RowTypeDescriptor = {
    ...simple("calendar-system", {
      label: "Calendar System",
      description:
        "Displays a calendar system (Gregorian sub-labels, Hebrew, Hijri, Persian, etc.) date for each column.",
      previewImageUrl: "",
      dataOwner: CALENDAR_SYSTEM_PROVIDER,
      nestable: true,
      layout: { cellHeight: 38, seeMoreHeight: 0 },
      cellLayoutOptions: {
        ...CELL_LAYOUT_DEFAULTS,
        disableContainment: true,
      },
    }),
    rendersChildren: () => false,
    buildLabel: (_lookupKey, attrs) =>
      Json.string(attrs, "calendarSystem") ?? "Calendar System",
    buildAriaLabel: () => "Calendar system row",
  };

  export const Text: RowTypeDescriptor = {
    ...simple("text", {
      label: "Text",
      description: "Provides rich text editing with markdown support.",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: "user",
      layout: {
        cellHeight: 200,
        stackable: true,
        spannable: true,
        // Render one cell per day column in day view (like number/checkmark)
        // instead of a week-wide span-stacked strip. `null` keeps text from
        // collapsing into a single full-width region when the sheet is zoomed
        // into days.
        minWidthTimescale: null,
      },
      nestable: true,
    }),
    heightBounds: DEFAULT_HEIGHT_BOUNDS,
    buildAriaLabel: (label, column, cellValue) => {
      const rowTitle = label ?? "Untitled";
      const dateStr = column.toString();
      if (cellValue == null || cellValue === "") {
        return `${rowTitle}, ${dateStr}: Empty`;
      }
      const text = String(cellValue);
      const truncated =
        text.length > 100 ? text.substring(0, 100) + "..." : text;
      return `${rowTitle}, ${dateStr}: ${truncated}`;
    },
  };

  export const Number: RowTypeDescriptor = {
    ...simple("number", {
      label: "Number",
      description: "Adds numeric cells with currency and formatting options.",
      previewImageUrl: `${IMG}/number@2x.png`,
      dataOwner: "user",
      layout: { stackable: true, spannable: true },
      nestable: true,
    }),
    heightBounds: DEFAULT_HEIGHT_BOUNDS,
    buildAriaLabel: (label, column, cellValue) => {
      const rowTitle = label ?? "Untitled";
      const dateStr = column.toString();
      if (cellValue == null) {
        return `${rowTitle}, ${dateStr}: Empty`;
      }
      return `${rowTitle}, ${dateStr}: ${String(cellValue)}`;
    },
  };

  export const Group = simple("group", {
    label: "Group",
    description: "Enables hierarchical organization of rows.",
    previewImageUrl: `${IMG}/formula@2x.png`,
    dataOwner: "layout",
    cellLayoutOptions: { background: "none", canDrawCardBorder: false },
    nestable: true,
  });

  export const Formula: RowTypeDescriptor = {
    ...simple("formula", {
      label: "Formula",
      description: "Adds formula support with calculation capabilities.",
      previewImageUrl: `${IMG}/formula@2x.png`,
      dataOwner: "user",
      layout: { spannable: true },
      nestable: true,
    }),
    // Default formula cells draw the standard SheetCell card background,
    // same as Number rows. `flat` formulas (system rows like the
    // day-of-month strip) skip the card entirely.
    cellLayoutOptions: (attributes) =>
      attributes?.style === "flat"
        ? { card: false, canDrawCardBorder: false }
        : null,
  };

  export const Checkmark: RowTypeDescriptor = {
    ...simple("checkmark", {
      label: "Checkmark",
      description: "Adds toggleable checkmark cells for task completion.",
      previewImageUrl: `${IMG}/check@2x.png`,
      dataOwner: "user",
      layout: { stackable: true, spannable: true },
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
      nestable: true,
    }),
    buildAriaLabel: (label, column, cellValue) => {
      const rowTitle = label ?? "Untitled";
      const dateStr = column.toString();
      const checked = cellValue === 1;
      return `${rowTitle}, ${dateStr}: ${checked ? "Checked" : "Unchecked"}`;
    },
  };

  export const Graph: RowTypeDescriptor = {
    ...simple("graph", {
      label: "Graph",
      description: "Creates interactive data visualizations from numeric data.",
      previewImageUrl: `${IMG}/graph@2x.png`,
      dataOwner: "user",
      layout: { cellHeight: 120 },
      cellLayoutOptions: {
        card: false,
        canDrawCardBorder: false,
        marginInline: "0px",
      },
    }),
    buildAriaLabel: (label, column, cellValue) => {
      const rowTitle = label ?? "Untitled";
      const dateStr = column.toString();
      if (cellValue == null) {
        return `${rowTitle}, ${dateStr}: Chart with no data`;
      }
      const data = cellValue as number[];
      if (!Array.isArray(data) || data.length === 0) {
        return `${rowTitle}, ${dateStr}: Chart with no data`;
      }
      const min = Math.min(...data);
      const max = Math.max(...data);
      const avg = data.reduce((a, b) => a + b, 0) / data.length;
      return `${rowTitle}, ${dateStr}: Chart showing ${data.length} points, range ${min} to ${max}, average ${avg.toFixed(1)}`;
    },
  };

  export const SchedulingLink: RowTypeDescriptor = {
    ...simple("scheduling-link", {
      label: "Scheduling Link",
      description: "Scheduling link with bookable availability time blocks.",
      previewImageUrl: `${IMG}/calendar@2x.png`,
      dataOwner: "user",
      nestable: true,
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    cellCardinality: "multi",
  };

  export const Calendar: RowTypeDescriptor = {
    ...simple("calendar", {
      label: "Calendar",
      description: "Renders calendar events with interactive editing.",
      previewImageUrl: `${IMG}/calendar@2x.png`,
      dataOwner: "layout",
      nestable: true,
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
      layout: {
        cellHeight: ({ heightPx, sheetTimescale, attributes }) => {
          // Config cascades from the body's override slot
          // (`children["calendar-body"]`) over the legacy top-level — the
          // visible row is the alias, so its height fn resolves the same
          // cascade the body's `timedConfig` does, from the alias's own bag.
          const attrs = TimedGridConfig.withChildOverride(
            attributes ?? {},
            TimedGridConfig.BODY_PRESET,
          );
          if (Json.string(attrs, "type") === "list") return 32;
          if (
            sheetTimescale === "month" &&
            !(Json.boolean(attrs, "disableMonthlyView") ?? false)
          ) {
            return heightPx ?? 927;
          }
          if (heightPx) return heightPx;
          const durationHrs = (Json.number(attrs, "endHr") ?? 24) -
            (Json.number(attrs, "startHr") ?? 0);
          return durationHrs * 48 + 44;
        },
      },
    }),
    defaultAttributes(_row: SheetRow.Input) {
      // The rows-side sheet tree is canonical. `SheetRowTree` keeps
      // the hydrated alias implementation children available for
      // drawer/inspection surfaces, while its rendered flatten
      // projects the calendar alias as the visible body/legend row.
      // The rows-side `calendarAliasFactory` template synthesizes
      // this structure plus a transitional `data-source
      // preset=calendar` sibling and the `secondary-timezones` alias.
      //
      // Hydrated shape:
      //   calendar (alias)
      //   └─ calendar-body (layout, binds to the timescale-polymorphic
      //      calendar-body plugin — timed timeline for week, month grid
      //      for month)
      //      └─ calendar-list (alias, surfaces events via aggregate)
      //
      // No `data-source preset=calendar` here — events render via
      // calendar-list's per-calendar dataSource children, walked by
      // `LIST_AGGREGATE_FACADE` and the per-shape event renderers
      // (`timeline[column].event` / `grid.event`, which coexist; the
      // calendar-body plugin selects which to paint). Secondary-tz
      // children land on the rows-side tree only; the calendar legend
      // walker reads `row.variant.children` (rows-side) to find them.
      return {
        type: "default",
        startHr: 0,
        endHr: 24,
        disableMonthlyView: false,
      };
    },
    childSpecs: () => CALENDAR_CHILD_SPECS,
    requiresHeaderSecondRow: (sheetTimescale, attributes) =>
      calendarHeaderSecondRowKind(sheetTimescale, attributes) != null,
    headerSecondRowKind: calendarHeaderSecondRowKind,
    initialScrollTop: (_heightPx, sheetTimescale, attributes) => {
      const attrs = TimedGridConfig.withChildOverride(
        attributes ?? {},
        TimedGridConfig.BODY_PRESET,
      );
      if (Json.string(attrs, "type") === "list") return 0;
      const isMonthly =
        sheetTimescale === "month" &&
        !(Json.boolean(attrs, "disableMonthlyView") ?? false);
      if (isMonthly) return 0;
      if ((Json.number(attrs, "startHr") ?? 0) === 0) return 9 * 48;
      return 0;
    },
    activeLayout: (timescale) =>
      timescale === "month" ? "calendarMonth" : "calendar",
    buildAriaLabel: (label, column) =>
      `${label ?? "Calendar"}, ${column.toString()}`,
  };

  // Logical shape (spannable + minWidthTimescale="week") sourced
  // from rows-side `resolveLayoutShape("calendar-list", ...)`.
  export const CalendarList: RowTypeDescriptor = {
    ...simple("calendar-list", {
      label: "Calendar list",
      description:
        "Display a chronological list of calendar events from all connected calendars.",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: {
        kind: "alias",
        batchKey: () => ":~lona:calendar:primary:events",
      },
      layout: { cellHeight: 200 },
    }),
    // No `layoutSpec` override needed: shape now resolves from the row's wire
    // type (`alias.calendar-list`) threaded through `simple()`'s closure, and
    // `layout.cellHeight: 200` above already yields `heightPx ?? 200`.
    cellCardinality: "multi",
  };

  /**
   * Per-calendar event SOURCE row — `:~google:{authId}:calendar:{calId}:events`,
   * the lona-native `:~lona:calendar:{calId}:events`, and the
   * `:~lona:calendar:primary:events` aggregate. All carry the server
   * type `calendar-events` (`tento_lona_connector::row_type::source::CALENDAR_EVENTS`).
   * The SDK opens this row's `RowCellCache` for event writes via
   * `EventsClient.opsForOrigin`.
   *
   * `multi`-cardinality is load-bearing: many events share a date,
   * disambiguated by `cell.id` (the event id), so every event cell —
   * fetched-datetime AND optimistic/all-day-date — routes to the custom
   * store keyed by id, never collapsed into the date-keyed standard
   * store. Before this registration the type fell through to the generic
   * single-cardinality fallback, which split an events row's cells across
   * two stores by provenance and let the `populateRow` rebuild resurrect
   * "deleted" events / revert edits (the "deletion is finicky" bug):
   * the clear targeted the date-keyed store while the fetched timed event
   * lived in the id-keyed one.
   */
  export const CalendarEvents: RowTypeDescriptor = {
    ...simple("calendar-events", {
      label: "Events",
      description: "Calendar event source row.",
      previewImageUrl: `${IMG}/calendar@2x.png`,
      dataOwner: "provider",
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    cellCardinality: "multi",
  };

  // `id` is the canonical wire type `timeline[row]` (was bare `"timeline"`).
  export const Timeline: RowTypeDescriptor = {
    ...simple("timeline[row]", {
      label: "Timeline",
      description:
        "Displays date-span items as horizontal bars in stacked rows.",
      previewImageUrl: `${IMG}/calendar-all-day@2x.png`,
      dataOwner: "layout",
      nestable: true,
      layout: { cellHeight: 32 },
      cellLayoutOptions: {
        card: false,
        canDrawCardBorder: false,
        padding: "0px",
        marginInline: "0px",
      },
    }),
    activeLayout: () => "timeline",
  };

  // Standalone `timeline[column]` body — the calendar-body's timed grid used
  // directly as a sheet row (no `alias.calendar` wrapper). Mirrors `Timeline`
  // but gives the cell the full timed height via a `Calendar`-style callback,
  // so a standalone timed grid and its subcolumn lanes aren't squished into
  // 32px. `id` is its canonical wire type `timeline[column]` (distinct from
  // `Timeline`'s `timeline[row]`) — shape now keys off the row's wire type, so
  // there's no shared-shape reason to alias the id. Registered for the
  // `timeline[column]` slot below; `timeline[row]` / all-day strips keep the
  // static-32 `Timeline`.
  export const TimelineColumn: RowTypeDescriptor = {
    ...simple("timeline[column]", {
      label: "Timeline",
      description:
        "Vertical time-down timed grid; hosts chart/event subcolumns as full-height lanes.",
      previewImageUrl: `${IMG}/calendar-all-day@2x.png`,
      dataOwner: "layout",
      nestable: true,
      layout: {
        cellHeight: ({ heightPx, attributes }) => {
          const attrs = attributes ?? null;
          if (Json.string(attrs, "type") === "list") return 32;
          if (heightPx != null) return heightPx;
          const durationHrs = (Json.number(attrs, "endHr") ?? 24) -
            (Json.number(attrs, "startHr") ?? 0);
          return durationHrs * 48 + 44;
        },
      },
      cellLayoutOptions: {
        card: false,
        canDrawCardBorder: false,
        padding: "0px",
        marginInline: "0px",
      },
    }),
    activeLayout: () => "timeline",
  };

  // `id` is the canonical wire type `grid` (was legacy `"calendar-month"`).
  export const CalendarMonth: RowTypeDescriptor = simple("grid", {
    label: "Calendar Month",
    description: "A monthly calendar view that updates based on viewport.",
    previewImageUrl: `${IMG}/calendar@2x.png`,
    dataOwner: "layout",
    layout: { cellHeight: 927 },
    cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
  });

  // `id` is the canonical wire type `alias.weather` (was legacy bare
  // `"weather"`): descriptor.id := wireType, so a joined weather row's
  // `descriptorType` names the same concrete type `normalizeParseRow`
  // stamps into `raw.type` (self-describing wire type, 1e24d60).
  export const Weather: RowTypeDescriptor = {
    // (`RowTypeCatalog.Alias` long-form: the `Alias` destructure at the
    // registration block below runs after this initializer.)
    ...simple(RowTypeCatalog.Alias.WEATHER, {
      label: "Weather",
      description: "Displays weather forecasts for selected locations.",
      previewImageUrl: `${IMG}/weather@2x.png`,
      dataOwner: "provider",
    }),
    dataKeys: ["lid"],
    nestable: false,
    // Logs sibling is hydrated by the rows-side `weather` alias
    // template (`tento/tento-lona-js/sheets/src/alias-templates-builtin.ts`'s
    // weather entry — `preset: "logs"`, `storage: "own"`). Both
    // sides converge on the same nested-key ("logs", since
    // `row-node.ts:225` strips the trailing `-N` for the nestedRows
    // override key — applies whether the preset was "logs-0" or
    // "logs"). The attribute that drove the old childSpec expansion must
    // be lifted into the alias props at hydrate time, or the two trees
    // disagree on which children exist.
  };

  export const Timezone: RowTypeDescriptor = {
    ...simple("timezone", {
      label: "Timezone",
      description:
        "Display multiple timezones side by side with visual indicators for the current time and hover state across all rows.",
      previewImageUrl: `${IMG}/number@2x.png`,
      dataOwner: "user",
      layout: { cellHeight: 48 },
      requiresHeaderSecondRow: true,
      cellLayoutOptions: { ...CELL_LAYOUT_DEFAULTS, labelHeightPx: 48 },
    }),
  };

  export const Block = simple("block", {
    label: "Block",
    description: "Enables block-based layout for grouping content.",
    previewImageUrl: `${IMG}/formula@2x.png`,
    dataOwner: "layout",
    cellLayoutOptions: {
      background: "red",
      card: false,
      canDrawCardBorder: false,
      padding: "0px",
      marginInline: "0px",
      marginBlock: "0px",
    },
  });

  export const Logs: RowTypeDescriptor = {
    ...simple("logs", {
      label: "Logs",
      description: "Displays log entries.",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: "provider",
      layout: { cellHeight: 200, spannable: true, minWidthTimescale: "week" },
      cellLayoutOptions: { marginInline: "0px", background: "none" },
    }),
  };

  export const Sticker = simple("sticker", {
    label: "Sticker",
    description: "Add decorative stickers to your calendar (iMessage-style)",
    previewImageUrl: `${IMG}/text@2x.png`,
    // `provider`, not `user`: `:~lona:stickers` is a server-backed reserved row
    // the client fetches on demand. With `user`, the bulk sheet fetch marks the
    // row "fetched" when the server omits it from the calendar batch (stickers
    // are a subcolumn, not a top-level row), suppressing the per-row fetch — so
    // sticker cells never load on the calendar. See `sheet-cell-accessor.ts`
    // `_shouldMarkMissingRowFetched` (:826) + the lookup-key fetch path (:260).
    dataOwner: "provider",
    layout: { cellHeight: 200 },
    cellLayoutOptions: { marginInline: "0px", background: "none" },
  });

  export const Debug = simple("debug", {
    label: "Debug",
    description: "",
    previewImageUrl: "",
    dataOwner: "user",
  });

  export const Task = simple("task", {
    label: "Task",
    description: "",
    previewImageUrl: "",
    dataOwner: "user",
  });

  export const Journal = simple("journal", {
    label: "Journal",
    description: "",
    previewImageUrl: "",
    dataOwner: "user",
  });

  export const Data: RowTypeDescriptor = {
    ...simple("data", {
      label: "Data",
      description: "Represents data",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: "provider",
      layout: {
        // Collapses to one label row when the raw JSON is hidden via the
        // legend eye; drag-resized heightPx applies only while visible.
        cellHeight: ({ heightPx, attributes }) =>
          JsonHide.isHiddenAttrs(attributes)
            ? JsonHide.HIDDEN_CELL_HEIGHT
            : (heightPx ?? 400),
        seeMoreHeight: 0,
      },
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    heightBounds: DEFAULT_HEIGHT_BOUNDS,
    defaultAttributes(_row: SheetRow.Input) {
      return {
        type: "chart",
        children: { "data-json": { visible: true } },
      };
    },
    // The static table's default reads the built-in presets; a client's
    // row registry rebinds it to that client's own (`RowType.inRegistries`).
    childSpecs: (row: SheetRow.Input) => dataChildSpecs(row, SheetRegistries.BUILTINS),
  };

  /** A `data` row's child seeds: its lookup key's presets in `registries`, then the raw-JSON row. */
  function dataChildSpecs(row: SheetRow.Input, registries: SheetRegistries): readonly ChildSpec[] {
    const specs = row.lookupKey ? registries.dataPresets.get(row.lookupKey) : null;
    const jsonSpec: DataPresetSpec = {
      type: "data-json",
      preset: "data-json",
    };
    return dataPresetToChildSpecs([...(specs ?? []), jsonSpec], null);
  }

  /**
   * `descriptor` as a client whose registries are `registries` resolves it:
   * the one registry-dependent member, a `data` row's preset-driven child
   * seeds, reads that client's data presets (`SheetRegistries.dataPresets`)
   * instead of the built-ins. Every other descriptor is returned as is.
   * `SheetRow.Registry` applies it to each row it registers.
   */
  export function inRegistries(descriptor: RowTypeDescriptor, registries: SheetRegistries): RowTypeDescriptor {
    if (descriptor !== Data || registries === SheetRegistries.BUILTINS) return descriptor;
    return deepFreeze({
      ...descriptor,
      childSpecs: (row: SheetRow.Input) => dataChildSpecs(row, registries),
    });
  }

  export const DataV1: RowTypeDescriptor = {
    ...simple("data.v1", {
      label: "Data",
      description: "Represents data",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: "provider",
      layout: { cellHeight: 200, spannable: true, minWidthTimescale: "week" },
    }),
    heightBounds: DEFAULT_HEIGHT_BOUNDS,
  };

  export const DataRenderer: RowTypeDescriptor = {
    ...simple("data-renderer", {
      label: "Renderer",
      description:
        "Defines how a data series is visually rendered (points, curves, bars, etc.).",
      previewImageUrl: `${IMG}/graph@2x.png`,
      dataOwner: "layout",
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
  };

  export const DataSeries: RowTypeDescriptor = {
    ...simple("data-series", {
      label: "Series",
      description: "Defines how to extract [x, y] points from raw JSON cells.",
      previewImageUrl: `${IMG}/graph@2x.png`,
      dataOwner: "provider",
      layout: { cellHeight: DATA_GRAPH_ROW_HEIGHTS.series },
      cellLayoutOptions: {
        ...CELL_LAYOUT_DEFAULTS,
        background: "var(--bg-surface-light)",
        card: true,
        marginInline: "3px",
      },
    }),
  };

  // `id` is the canonical wire type `canvas` (was legacy `"data-canvas"`).
  export const DataCanvas: RowTypeDescriptor = {
    ...simple("canvas", {
      label: "Canvas",
      description:
        "Defines the coordinate system and rendering configuration for a data graph.",
      previewImageUrl: `${IMG}/graph@2x.png`,
      dataOwner: "layout",
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
  };

  export const DataJson: RowTypeDescriptor = {
    ...simple("data-json", {
      label: "Data JSON",
      description: "Raw JSON data list for a data row.",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: "provider",
      layout: {
        // Same collapse-on-hide as `Data` — see JsonHide.
        cellHeight: ({ heightPx, attributes }) =>
          JsonHide.isHiddenAttrs(attributes)
            ? JsonHide.HIDDEN_CELL_HEIGHT
            : (heightPx ?? 600),
      },
    }),
  };

  export const Fallback = simple("fallback", {
    label: "Fallback",
    description: "Placeholder for unsupported or unmapped row types.",
    previewImageUrl: `${IMG}/calendar@2x.png`,
    dataOwner: "user",
    cellLayoutOptions: {},
  });

  /**
   * `list` — computed aggregate source row. Hydrated under
   * `calendar-list` (and other future aggregating templates) by the
   * rows-side alias factory; its children are infrastructure
   * (per-calendar dataSources) that should not appear in the visible
   * flat list. The aggregate paints its OWN merged stream of cells
   * via the per-slot data-renderer children attached to it; the
   * source children stay hidden.
   *
   * `rendersChildren: () => false` makes `SheetRowTree.visit` stop
   * descending into this row so the binder never tries to dispatch
   * the per-calendar dataSources to a renderer plugin (they have no
   * default-renderer child and would crash `column.calendar`'s
   * resolver — see `tento/tento-lona-js/sheets-ui/src/sheet/binder/default-binder-helpers.ts:752`).
   */
  export const List: RowTypeDescriptor = {
    ...simple("list", {
      label: "List",
      description: "Aggregate list of cells from child sources.",
      previewImageUrl: `${IMG}/calendar@2x.png`,
      dataOwner: "layout",
      cellLayoutOptions: {},
    }),
    rendersChildren: () => false,
  };

  export const Label: RowTypeDescriptor = {
    ...simple("label", {
      label: "Label",
      description: "A simple sticky label.",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: "user",
      layout: { cellHeight: 80 },
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    heightBounds: DEFAULT_HEIGHT_BOUNDS,
  };

  export const SheetFooter = simple("sheet-footer", {
    label: "Footer",
    description: "Sheet footer with action bar.",
    previewImageUrl: "",
    dataOwner: "layout",
    layout: {
      cellHeight: 48,
      maxStackHeight: null,
      maxStackedCells: null,
      seeMoreHeight: 0,
      stackable: false,
      spannable: false,
    },
    cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
  });

  /**
   * Server-backed surface container — the unique top-level row
   * for a sheet's header / footer surface (after Phase D).
   * Intrinsically transparent: zero `cellHeight` (children
   * render at the surface row's own offset, no gap), no plugin
   * (skipped from flat-output emission via `tree-flatten.ts`'s
   * `isSurfaceRow` guard). The server stamps
   * `metadata.surface = "header" | "footer"` to discriminate
   * which surface; the row type itself just encodes "this is a
   * transparent layout container."
   *
   * Header and footer each have one anchor row; body is implicit.
   */
  export const SurfaceContainer: RowTypeDescriptor = {
    ...simple("surface-container", {
      label: "Surface",
      description: "Transparent container for a sheet's header / footer surface.",
      previewImageUrl: "",
      dataOwner: "layout",
      layout: {
        cellHeight: () => 0,
        maxStackHeight: null,
        maxStackedCells: null,
        seeMoreHeight: 0,
        stackable: false,
        spannable: false,
      },
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    role: "inert",
    kind: "surface-container",
  };

  /**
   * Per-surface container (`header` / `body` / `footer`) — synthesized
   * by `SurfaceProjection.project`, and authorable directly by
   * raw-sheet templates. Transparent like `SurfaceContainer`. The
   * surface target lives in `attributes.surface`; the rows-side
   * `ROW_TYPE_SPECS` seeds `surface: "body"` for bare authored rows
   * (instance wins in `reconcileAttributes`) — mirror that default
   * here so the SDK row's reconciled attributes satisfy the inert
   * surface plugin's required attribute instead of falling through to
   * the generic fallback descriptor and crashing the bind.
   */
  export const Surface: RowTypeDescriptor = {
    ...simple("surface", {
      label: "Surface",
      description:
        "Container for one sheet surface (header / body / footer).",
      previewImageUrl: "",
      dataOwner: "layout",
      layout: {
        cellHeight: () => 0,
        maxStackHeight: null,
        maxStackedCells: null,
        seeMoreHeight: 0,
        stackable: false,
        spannable: false,
      },
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    role: "inert",
    kind: "surface",
    defaultAttributes(_row: SheetRow.Input) {
      return { surface: "body" };
    },
  };

  /**
   * Surface projection — the structural relocator the `alias.calendar`
   * factory synthesizes for the all-day header strip
   * (`projection [surface=header]`, see `builtin-alias-factories.ts`).
   * Like `Surface`, it is a transparent inert container; `SheetTree`
   * drops it (its children are hoisted onto the dedicated surface row,
   * see `sheet-tree.ts` — `InertRow` + `kind === "projection"`), so it
   * never reaches the cell binder. Registered here so a synthesized
   * `projection` row hydrates as `InertRow { kind: "projection" }` and
   * is subsumed — without it the row falls through to the generic
   * fallback and renders as `Unknown Plugin: "projection"` (the same
   * failure the `Surface` descriptor above was added to prevent).
   */
  export const Projection: RowTypeDescriptor = {
    ...simple("projection", {
      label: "Projection",
      description:
        "Structural relocator that hoists its child onto a sheet surface.",
      previewImageUrl: "",
      dataOwner: "layout",
      layout: {
        cellHeight: () => 0,
        maxStackHeight: null,
        maxStackedCells: null,
        seeMoreHeight: 0,
        stackable: false,
        spannable: false,
      },
      cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
    }),
    role: "inert",
    kind: "projection",
  };

  export const Tasks: RowTypeDescriptor = {
    ...simple("tasks", {
      label: "Tasks",
      description: "Integrates task management with due date tracking.",
      previewImageUrl: `${IMG}/text@2x.png`,
      dataOwner: "provider",
      layout: { cellHeight: 160, spannable: true, minWidthTimescale: "week" },
      cellLayoutOptions: { canDrawCardBorder: false },
    }),
    // Each task is its own cell keyed by task id; multiple tasks can share
    // the same anchor date, so the store must preserve them all instead of
    // collapsing by date.
    cellCardinality: "multi",
  };

  export const TimelineLegend = simple("timeline-legend", {
    label: "Timeline",
    description: "Horizontal timeline ruler with ticks.",
    previewImageUrl: "",
    dataOwner: "layout",
    cellLayoutOptions: CELL_LAYOUT_DEFAULTS,
  });

  export const Video: RowTypeDescriptor = {
    ...simple("video", {
      label: "Video",
      description: "Embeds and plays YouTube videos in the sheet.",
      previewImageUrl: `${IMG}/video@2x.png`,
      dataOwner: "user",
      layout: { cellHeight: 640, spannable: true, minWidthTimescale: "week" },
    }),
    heightBounds: DEFAULT_HEIGHT_BOUNDS,
  };

  // Canonical wire-type keys reference the generated catalog; the bare
  // back-compat spellings below (calendar-allday, timeline-row) stay literal
  // and drop at contract.
  const { Alias, Layout } = RowTypeCatalog;

  const registry = new Map<string, RowTypeDescriptor>();
  for (const d of [
    Header,
    CalendarSystem,
    Text,
    Number,
    Group,
    Formula,
    Checkmark,
    Graph,
    SchedulingLink,
    CalendarList,
    CalendarEvents,
    Timeline,
    CalendarMonth,
    Weather,
    Timezone,
    Block,
    Logs,
    Sticker,
    Debug,
    Task,
    Journal,
    Data,
    DataV1,
    DataRenderer,
    DataSeries,
    DataCanvas,
    DataJson,
    Fallback,
    List,
    Label,
    SheetFooter,
    SurfaceContainer,
    Surface,
    Projection,
    Tasks,
    TimelineLegend,
    Video,
  ]) {
    registry.set(d.id, d);
  }
  // Backward compat: "calendar-allday" maps to Timeline
  registry.set("calendar-allday", Timeline);
  // The all-day projection rides the wire as `projection[row]` too
  // (rows-side `raw-sheet.ts` maps both to the same inert kind); keep
  // parity so either form hydrates as the inert projection container.
  registry.set("projection[row]", Projection);

  // Phase 4a: alias kinds are namespaced `alias.*` on the wire (both the
  // migrated `type_` and the `type="alias"` + `attributes.template` form).
  // Register the SDK alias descriptors under their canonical template names
  // so `RowType.get("alias.calendar")` (and alias-row resolution by template)
  // returns the real descriptor — with its cellHeight — instead of the
  // generic fallback. The retired bare `calendar` id intentionally falls
  // through to RowType.fallback so stale rows show the Unknown Plugin surface.
  registry.set(Alias.CALENDAR, Calendar);
  registry.set(Alias.CALENDAR_LIST, CalendarList);
  registry.set(Alias.CALENDAR_ALLDAY, Timeline);
  registry.set(Alias.WEATHER, Weather);
  registry.set(Alias.TIMEZONE, Timezone);
  registry.set(Alias.TASK, Task);
  registry.set(Alias.TASK_LIST, Tasks);
  // `Weather.id` is now the canonical `alias.weather` (descriptor.id :=
  // wireType), so the main loop no longer registers it under the legacy
  // bare `"weather"`. Keep that key resolving: the parse boundary still
  // bridges bare weather rows (client.ts `aliasTemplateName`), and rows
  // that bypass parse normalization (standalone `SheetRow.from`) must
  // not collapse to the 32px fallback. Unlike the retired bare
  // `calendar` (intentionally loud above), bare weather remains a
  // tolerated legacy shape.
  registry.set("weather", Weather);
  // Bare `alias` is a LEGAL wire form, not a legacy one: the open template
  // tier (non-catalog templates — `alias.chart`, `alias.stickers`,
  // `alias.default-weather`, per-sheet overlays) rides the wire as
  // `type: "alias"` + `attributes.template` by design (`normalizeParseRow`
  // rewrites non-catalog templates back to bare). Register a real
  // descriptor so those rows stop tripping the unregistered-type warning.
  // `dataOwner: "user"` is deliberate — it is byte-for-byte what the
  // `fallback()` these rows resolved through already returned, and the
  // trace says it's the only safe generic choice: "layout" would turn the
  // per-row cell fetch into `Promise.resolve([])` for every open-tier row
  // (sheet-cell-accessor `_installFetch`), and open-tier templates are
  // heterogeneous (default-weather's catalog sibling is a fetching
  // "provider"); "user" also keeps freshly-inserted alias rows vacuously
  // covered (`shouldMarkInsertedRowKnownEmpty`), while lookup-keyed rows
  // are protected either way by the `lookupKey != null` branch of
  // `_shouldMarkMissingRowFetched`. Per-template metadata (panel label)
  // still comes from the template registry's `displayName`.
  registry.set(
    Alias.ALIAS,
    simple(Alias.ALIAS, {
      label: "Alias",
      description: "A row expanded from an alias template.",
      previewImageUrl: "",
      dataOwner: "user",
    }),
  );

  // Phase 4b: layout kinds are canonical on the wire (`grid`, `timeline[row]`,
  // `timeline[column]`, `canvas`). The SDK descriptors were keyed by their old
  // names (`calendar-month`, `timeline`, `data-canvas`), so `RowType.get("grid")`
  // / `("timeline[column]")` missed → generic 32px fallback → the calendar's
  // layout child (and the calendar) collapsed. Register the canonical keys so
  // they resolve to the real descriptor + cellHeight (e.g. 927px month grid).
  registry.set(Layout.GRID, CalendarMonth);
  registry.set(Layout.TIMELINE_ROW, Timeline);
  registry.set(Layout.TIMELINE_COLUMN, TimelineColumn);
  registry.set(Layout.CANVAS, DataCanvas);

  // Renderer rows ride the wire under their layer-qualified id
  // (`column.text`, `canvas.line`, `timeline[row].event`, ...): the catalog's
  // renderer role. The descriptor is the generic renderer's, whatever the
  // layer — `data-renderer` stays registered for rows written before the
  // qualified ids.
  for (const entry of RowTypeCatalog.ALL) {
    if (entry.role === "renderer" && !registry.has(entry.ty)) {
      registry.set(entry.ty, DataRenderer);
    }
  }

  // Every built-in, frozen whole: `RowType.X` and `RowType.get(type)` are
  // process-global, shared by every client.
  for (const descriptor of registry.values()) deepFreeze(descriptor);

  export function isResizable(type: string): boolean {
    return get(type).heightBounds != null;
  }

  /** The built-in descriptor of `type`, or the 32px `fallback` for a type
   *  none is registered for (`warnUnregistered` reports those). */
  export function get(type: string): RowTypeDescriptor {
    return registry.get(type) ?? fallback(type);
  }

  /**
   * Warn — once per type, deduped through `warned`, the owning
   * `SheetRow.Registry`'s set — that `type` has no registered descriptor and
   * collapses to the 32px fallback. Loud, non-fatal: a miss silently
   * returned the fallback before, which made an unregistered or legacy wire
   * form (e.g. the dash `timeline-column`) look like a rendering bug.
   */
  export function warnUnregistered(type: string, warned: Set<string>): void {
    if (registry.has(type) || warned.has(type)) return;
    warned.add(type);
    Tracing.warn("ui")(
      `RowType.get: no descriptor registered for row type "${type}" — ` +
        `collapsing to the 32px fallback. Author the canonical wire form ` +
        `(e.g. "timeline[column]", not "timeline-column"), or register it ` +
        `in row-descriptor.ts.`,
    );
  }

  /** Fallback descriptor for unknown row types. */
  export function fallback(type: string): RowTypeDescriptor {
    return simple(type, {
      label: type,
      description: "",
      previewImageUrl: "",
      dataOwner: "user",
    });
  }

  /**
   * Caller-constructed descriptor registry. Allows overriding built-in
   * descriptors with custom ones that hold references to stores/preferences.
   */
  export class Registry {
    readonly #map = new Map<string, RowTypeDescriptor>();

    register(descriptor: RowTypeDescriptor): this {
      this.#map.set(descriptor.id, descriptor);
      return this;
    }

    get(type: string): RowTypeDescriptor {
      return this.#map.get(type) ?? RowType.fallback(type);
    }

    has(type: string): boolean {
      return this.#map.has(type);
    }
  }

  /** Create a registry pre-populated with all built-in descriptors. */
  export function createDefaultRegistry(): Registry {
    const reg = new Registry();
    for (const [, d] of registry) reg.register(d);
    return reg;
  }
}
